import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Form, Input, Select, Modal, Descriptions } from 'antd';
import { SearchOutlined, EyeOutlined, SwapOutlined } from '@ant-design/icons';
import { transferOrderService, TransferOrder } from '../../services/paymentService';

const { Option } = Select;

const TransferOrderPage: React.FC = () => {
  const [orders, setOrders] = useState<TransferOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [searchForm] = Form.useForm();
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<TransferOrder | null>(null);

  const loadOrders = async (page = 1, pageSize = 10, params = {}) => {
    try {
      setLoading(true);
      const response = await transferOrderService.list({ page, pageSize, ...params });
      const list = response?.data?.list || response?.list || [];
      const total = response?.data?.total || response?.total || 0;
      setOrders(list);
      setPagination({ page, pageSize, total });
    } catch (error) {
      console.error('加载转账订单失败', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrders(); }, []);

  const statusMap: Record<string, { label: string; color: string }> = {
    'pending': { label: '待处理', color: 'default' },
    'processing': { label: '处理中', color: 'processing' },
    'success': { label: '转账成功', color: 'success' },
    'failed': { label: '转账失败', color: 'error' }
  };

  const channelMap: Record<string, { label: string; color: string }> = {
    'alipay': { label: '支付宝', color: 'blue' },
    'wechat': { label: '微信', color: 'green' },
    'bank': { label: '银行卡', color: 'orange' }
  };

  const columns = [
    { title: '转账单号', dataIndex: 'transferNo', key: 'transferNo', width: 180 },
    { title: '应用', dataIndex: 'appCode', key: 'appCode', width: 100 },
    { 
      title: '转账渠道', dataIndex: 'channel', key: 'channel', width: 100,
      render: (channel: string) => {
        const config = channelMap[channel] || { label: channel, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    { title: '转账标题', dataIndex: 'title', key: 'title', ellipsis: true },
    { title: '转账金额', dataIndex: 'amount', key: 'amount', width: 100, render: (v: number) => `¥${v?.toFixed(2) || '0.00'}` },
    { title: '收款人', dataIndex: 'payeeName', key: 'payeeName', width: 100 },
    { title: '收款账号', dataIndex: 'payeeAccount', key: 'payeeAccount', width: 150, ellipsis: true },
    { 
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (status: string) => {
        const config = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    { title: '操作人', dataIndex: 'operatorName', key: 'operatorName', width: 100 },
    { title: '转账时间', dataIndex: 'transferredAt', key: 'transferredAt', width: 160, render: (v: string) => v || '-' },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 160 },
    {
      title: '操作', key: 'action', width: 80,
      render: (_: unknown, record: TransferOrder) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => { setCurrentOrder(record); setDetailVisible(true); }}>详情</Button>
      )
    }
  ];

  return (
    <Card title={<><SwapOutlined /> 转账订单</>}>
      <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }} onFinish={(v) => loadOrders(1, pagination.pageSize, v)}>
        <Form.Item name="transferNo"><Input placeholder="转账单号" allowClear style={{ width: 180 }} /></Form.Item>
        <Form.Item name="channel">
          <Select placeholder="转账渠道" allowClear style={{ width: 120 }}>
            <Option value="alipay">支付宝</Option>
            <Option value="wechat">微信</Option>
            <Option value="bank">银行卡</Option>
          </Select>
        </Form.Item>
        <Form.Item name="status">
          <Select placeholder="状态" allowClear style={{ width: 120 }}>
            <Option value="pending">待处理</Option>
            <Option value="processing">处理中</Option>
            <Option value="success">转账成功</Option>
            <Option value="failed">转账失败</Option>
          </Select>
        </Form.Item>
        <Form.Item name="payeeName"><Input placeholder="收款人" allowClear style={{ width: 120 }} /></Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
            <Button onClick={() => { searchForm.resetFields(); loadOrders(); }}>重置</Button>
          </Space>
        </Form.Item>
      </Form>
      <Table dataSource={orders} columns={columns} rowKey="id" loading={loading} scroll={{ x: 1400 }}
        pagination={{ current: pagination.page, pageSize: pagination.pageSize, total: pagination.total, showSizeChanger: true, showTotal: (t) => `共 ${t} 条`, onChange: (p, s) => loadOrders(p, s) }} />
      <Modal title="转账详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={600}>
        {currentOrder && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="转账单号">{currentOrder.transferNo}</Descriptions.Item>
            <Descriptions.Item label="应用">{currentOrder.appCode || '-'}</Descriptions.Item>
            <Descriptions.Item label="转账渠道">{channelMap[currentOrder.channel]?.label || currentOrder.channel}</Descriptions.Item>
            <Descriptions.Item label="转账类型">{currentOrder.transferType || '-'}</Descriptions.Item>
            <Descriptions.Item label="转账标题" span={2}>{currentOrder.title || '-'}</Descriptions.Item>
            <Descriptions.Item label="转账金额">¥{currentOrder.amount?.toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="状态"><Tag color={statusMap[currentOrder.status]?.color}>{statusMap[currentOrder.status]?.label}</Tag></Descriptions.Item>
            <Descriptions.Item label="收款人">{currentOrder.payeeName || '-'}</Descriptions.Item>
            <Descriptions.Item label="收款账号">{currentOrder.payeeAccount || '-'}</Descriptions.Item>
            <Descriptions.Item label="第三方交易号">{currentOrder.tradeNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="操作人">{currentOrder.operatorName || '-'}</Descriptions.Item>
            <Descriptions.Item label="转账时间">{currentOrder.transferredAt || '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{currentOrder.createdAt}</Descriptions.Item>
            {currentOrder.errorMsg && <Descriptions.Item label="错误信息" span={2}><span style={{ color: '#ff4d4f' }}>{currentOrder.errorMsg}</span></Descriptions.Item>}
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
};

export default TransferOrderPage;
